import React, { useState, useEffect } from 'react';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { Fragment } from 'react';
import { FaTimes } from 'react-icons/fa';

const SupportModuleForm = ({ isOpen, onClose, onSubmit, supportModule, projects, isLoading }) => {
  const [formData, setFormData] = useState({
    projectId: '',
    notificationEmail: ''
  });

  const [errors, setErrors] = useState({});

  // Debug: Log projects prop
  useEffect(() => {
    console.log('SupportModuleForm projects prop:', projects);
    console.log('SupportModuleForm projects length:', projects?.length || 0);
  }, [projects]);

  // Get user email from localStorage
  const getUserEmail = () => {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      return user?.email || '';
    } catch (error) {
      console.error('Error getting user email:', error);
      return '';
    }
  };

  useEffect(() => {
    if (supportModule) {
      setFormData({
        projectId: supportModule.projectId || '',
        notificationEmail: supportModule.settings?.notificationEmail || ''
      });
    } else {
      // For new support module, auto-fill with user's email
      const userEmail = getUserEmail();
      setFormData({
        projectId: '',
        notificationEmail: userEmail
      });
    }
    setErrors({});
  }, [supportModule, isOpen]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };


  const validateForm = () => {
    const newErrors = {};
    const activeProjects = projects.filter(project => project.status === 'active');

    if (!formData.projectId) {
      newErrors.projectId = 'Please select an active project';
    } else if (activeProjects.length === 0) {
      newErrors.projectId = 'No active projects available. Please create an active project first.';
    }

    if (!formData.notificationEmail.trim()) {
      newErrors.notificationEmail = 'Notification email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.notificationEmail)) {
      newErrors.notificationEmail = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      // Get selected project name for support module name
      const selectedProject = projects.find(p => p._id === formData.projectId);
      const moduleName = selectedProject ? `${selectedProject.name} Support` : 'Support Module';
      
      // Format data for API
      const submitData = {
        name: moduleName,
        projectId: formData.projectId,
        isActive: true,
        settings: {
          notificationEmail: formData.notificationEmail
        }
      };
      onSubmit(submitData);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-gray-900 border border-gray-700 p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-6">
                  <DialogTitle as="h3" className="text-lg font-medium text-white">
                    {supportModule ? 'Edit Support Module' : 'Create Support Module'}
                  </DialogTitle>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    <FaTimes className="h-5 w-5" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Basic Information */}
                  <div className="space-y-4">
                    <h4 className="text-md font-medium text-white">Support Module Setup</h4>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Project *
                      </label>
                      <select
                        value={formData.projectId}
                        onChange={(e) => handleInputChange('projectId', e.target.value)}
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      >
                        <option value="">Select an active project</option>
                        {projects.length === 0 ? (
                          <option value="" disabled>No projects available</option>
                        ) : (
                          projects.filter(project => project.status === 'active').map((project) => (
                            <option key={project._id} value={project._id}>
                              {project.name} - {project.companyName}
                            </option>
                          ))
                        )}
                      </select>
                      {errors.projectId && <p className="text-red-400 text-sm mt-1">{errors.projectId}</p>}
                      {projects.filter(project => project.status === 'active').length === 0 && (
                        <p className="text-yellow-400 text-sm mt-1">No active projects available. Please create an active project first.</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Notification Email *
                      </label>
                      <input
                        type="email"
                        value={formData.notificationEmail}
                        onChange={(e) => handleInputChange('notificationEmail', e.target.value)}
                        disabled={!supportModule} // Disable for new support modules
                        className={`w-full px-3 py-2 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                          !supportModule ? 'bg-gray-700 cursor-not-allowed opacity-60' : 'bg-gray-800'
                        }`}
                        placeholder="Enter email for support notifications"
                      />
                      {!supportModule && (
                        <p className="text-gray-400 text-xs mt-1">
                          Email is automatically set to your account email
                        </p>
                      )}
                      {errors.notificationEmail && <p className="text-red-400 text-sm mt-1">{errors.notificationEmail}</p>}
                    </div>
                  </div>



                  {/* Form Actions */}
                  <div className="flex justify-end space-x-3 pt-6 border-t border-gray-700">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-6 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isLoading || projects.filter(project => project.status === 'active').length === 0}
                      className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isLoading ? 'Saving...' : (supportModule ? 'Update' : 'Create')}
                    </button>
                  </div>
                </form>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default SupportModuleForm;